'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import { MarketingSeoContentSection } from '@/components/section';
import { HomeFaqPreviewSection, HomeLeadCtaSection, OutcomesMetricsSection } from '@/components/section';
import { HighlightIcon } from '@/assets/icons';
import MarketingHubHero from '@/components/pages/marketing/shared/MarketingHubHero';
import type { SpecialContentBlock } from '@/components/pages/marketing/special/types';
import type { FAQItem } from '@/components/section';
import type { TrustMetric } from '@/constants/homeMarketing';
import {
  PAGE_SECTION_BAND_ACCENT,
  PAGE_SECTION_BAND_ELEVATED,
  PAGE_SECTION_BAND_MUTED,
  pageSectionShell,
} from '@/constants/pageSections';
import { H3, P, Span, surfaceCardInteractive } from '@/components/ui';

export type MarketingHighlight = {
  title: string;
  text: string;
  icon?: string;
};

export type MarketingPageContentProps = {
  contentHeadingId: string;
  /** Marketing path for hero illustration (e.g. `ai-agents/conversational-ai`). */
  illustrationPath: string;
  pageTitle: string;
  kicker: string;
  intro: string;
  highlightsTitle: string;
  highlightsSubtitle: string;
  highlights: readonly MarketingHighlight[];
  metricsTitle: string;
  metricsSubtitle: string;
  metrics: readonly TrustMetric[];
  faqTitle: string;
  faqSubtitle: string;
  faqItems: readonly FAQItem[];
  leadCtaTitle: string;
  leadCtaBody: string;
  consultCta: string;
  servicesBrowseCta: string;
  seoSectionsTitle: string;
  seoSectionsSubtitle: string;
  seoSections: readonly SpecialContentBlock[];
};

/**
 * Client body for marketing inner pages — same hero band and section rhythm as home.
 */
export default function MarketingPageContent({
  contentHeadingId,
  illustrationPath,
  pageTitle,
  kicker,
  intro,
  highlightsTitle,
  highlightsSubtitle,
  highlights,
  metricsTitle,
  metricsSubtitle,
  metrics,
  faqTitle,
  faqSubtitle,
  faqItems,
  leadCtaTitle,
  leadCtaBody,
  consultCta,
  servicesBrowseCta,
  seoSectionsTitle,
  seoSectionsSubtitle,
  seoSections,
}: MarketingPageContentProps) {
  return (
    <article>
      <MarketingHubHero
        headingId={contentHeadingId}
        kicker={kicker}
        title={pageTitle}
        intro={intro}
        consultLabel={consultCta}
        secondaryLabel={servicesBrowseCta}
        secondaryHref="/services"
        illustrationPath={illustrationPath}
        variant="split"
      />

      <section
        className={pageSectionShell(PAGE_SECTION_BAND_ELEVATED)}
        aria-labelledby="marketing-highlights-heading"
      >
        <div className="page-heading-wrap" id="marketing-highlights-heading">
          <Heading2
            title={highlightsTitle}
            subtitle={highlightsSubtitle}
            titleClassName="text-prose"
            subtitleClassName="mt-3 text-prose-muted"
          />
        </div>
        <div className="page-content-grid page-grid-3">
          {highlights.map((h, i) => (
            <article
              key={i}
              className={surfaceCardInteractive('text-start')}
            >
              {h.icon ? (
                <Span className="page-highlight-icon" aria-hidden>
                  <HighlightIcon name={h.icon} />
                </Span>
              ) : null}
              <H3 className="page-card-title mt-3">{h.title}</H3>
              <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted">{h.text}</P>
            </article>
          ))}
        </div>
      </section>

      <OutcomesMetricsSection title={metricsTitle} subtitle={metricsSubtitle} metrics={metrics} />

      {seoSections.length > 0 ? (
        <MarketingSeoContentSection
          title={seoSectionsTitle}
          subtitle={seoSectionsSubtitle}
          sections={seoSections}
          className={clsx(PAGE_SECTION_BAND_MUTED, 'page-seo-content')}
        />
      ) : null}

      <HomeFaqPreviewSection
        title={faqTitle}
        subtitle={faqSubtitle}
        items={faqItems}
        className={PAGE_SECTION_BAND_ELEVATED}
      />

      <HomeLeadCtaSection
        title={leadCtaTitle}
        body={leadCtaBody}
        primaryHref="/consultation"
        primaryLabel={consultCta}
        secondaryHref="/services"
        secondaryLabel={servicesBrowseCta}
        className={clsx(PAGE_SECTION_BAND_ACCENT, '!min-h-0')}
      />
    </article>
  );
}
